import {t, tpl} from '../../i18n/i18n.js';
import {AppearanceDiagram, DiagramSlot} from '../../core/vocabulary.js';

/**
 * DOM builders for the cards of the section editor: the card frame shared by plain sections
 * and repeatable section items, the choice tiles, and the item menu, delete confirmation and
 * add controls. The builders hold no state; the section editor wires their callbacks.
 */

/**
 * @typedef {import('../../shell/types/user.types.js').CompositionSection} CompositionSection
 * @typedef {{
 *   card: HTMLLIElement,
 *   header: HTMLDivElement,
 *   summary: HTMLDivElement,
 *   label: HTMLSpanElement,
 *   actions: HTMLDivElement,
 *   edit: HTMLButtonElement,
 *   body: HTMLDivElement,
 * }} CardFrame
 */

/**
 * The card of one entry: drag handle, thumbnail, summary (label, status, choice, placement,
 * excerpt and, for plain sections, the visibility toggle slot), move and edit buttons, and a
 * collapsed body.
 *
 * @param {{
 *   entry: string,
 *   sectionId: string,
 *   labelText: string,
 *   withToggle: boolean,
 *   isDragging: () => boolean,
 *   onDragStart: (event: DragEvent) => void,
 *   onDragEnd: () => void,
 *   onMove: (delta: number, focusSelector: string) => void,
 *   onToggle: (expanded: boolean) => void,
 * }} options
 * @returns {CardFrame}
 */
export function createCardFrame({entry, sectionId, labelText, withToggle, isDragging, onDragStart, onDragEnd, onMove, onToggle}) {
    const card = document.createElement('li');
    card.className = 'section-card';
    card.dataset.sectionId = sectionId;
    card.addEventListener('dragstart', onDragStart);
    card.addEventListener('dragend', onDragEnd);

    const header = document.createElement('div');
    header.className = 'section-card-header';

    const handle = document.createElement('button');
    handle.type = 'button';
    handle.className = 'section-drag-handle';
    handle.tabIndex = -1;
    handle.setAttribute('aria-hidden', 'true');
    handle.title = tpl('pageForm.sectionDragHandle', {label: labelText});
    const gripIcon = document.createElement('i');
    gripIcon.className = 'fa-solid fa-grip-vertical';
    handle.appendChild(gripIcon);
    handle.addEventListener('pointerdown', () => {
        card.draggable = true;
    });
    handle.addEventListener('pointerup', () => {
        if (!isDragging()) card.draggable = false;
    });

    const summary = document.createElement('div');
    summary.className = 'section-card-summary';
    const label = document.createElement('span');
    label.className = 'section-card-label';
    label.id = `section-label-${entry}`;
    label.textContent = labelText;
    const status = document.createElement('span');
    status.className = 'section-card-status';
    const choiceSummary = document.createElement('span');
    choiceSummary.className = 'section-card-choice';
    choiceSummary.hidden = true;
    const excerpt = document.createElement('span');
    excerpt.className = 'section-card-excerpt';
    const thumbnail = document.createElement('img');
    thumbnail.className = 'section-card-thumbnail';
    thumbnail.alt = '';
    thumbnail.hidden = true;
    const placement = document.createElement('span');
    placement.className = 'section-card-placement';
    placement.hidden = true;
    summary.append(label, status, choiceSummary, placement, excerpt);
    if (withToggle) {
        const toggleSlot = document.createElement('div');
        toggleSlot.className = 'section-card-toggle';
        summary.appendChild(toggleSlot);
    }

    const actions = document.createElement('div');
    actions.className = 'section-card-actions';
    const up = iconButton('section-move-up', 'fa-arrow-up');
    const down = iconButton('section-move-down', 'fa-arrow-down');
    up.addEventListener('click', () => onMove(-1, '.section-move-up'));
    down.addEventListener('click', () => onMove(1, '.section-move-down'));

    const bodyId = `section-body-${entry}`;
    const edit = document.createElement('button');
    edit.type = 'button';
    edit.className = 'btn btn-outline section-edit';
    edit.setAttribute('aria-expanded', 'false');
    edit.setAttribute('aria-controls', bodyId);
    edit.setAttribute('aria-describedby', label.id);
    edit.addEventListener('click', () => onToggle(edit.getAttribute('aria-expanded') === 'true'));
    actions.append(up, down, edit);

    header.append(handle, thumbnail, summary, actions);

    const body = document.createElement('div');
    body.className = 'section-card-body';
    body.id = bodyId;
    body.hidden = true;

    card.append(header, body);
    return {card, header, summary, label, actions, edit, body};
}

/**
 * The layout and appearance choices of a section, for the plain section or for one item.
 * Radio names carry the entry, so every card has its own groups.
 *
 * @param {CompositionSection} section
 * @param {string} entry
 * @param {{layout: (id: string) => void, appearance: (id: string) => void}} onSelect
 * @returns {HTMLFieldSetElement[]}
 */
export function createChoiceGroups(section, entry, onSelect) {
    const groups = [];
    if (section.layouts.length > 1) {
        groups.push(createChoiceGroup(entry, 'layout', 'pageForm.sectionLayout', section.layouts,
            (layout) => layoutDiagram(layout.diagram), onSelect.layout));
    }
    if (Array.isArray(section.appearances) && section.appearances.length > 0) {
        groups.push(createChoiceGroup(entry, 'appearance', 'pageForm.sectionAppearance', section.appearances,
            (appearance) => appearanceDiagram(appearance.diagram), onSelect.appearance));
    }
    return groups;
}

/**
 * The row background choice: one tile per declared band. `name` makes the radio group unique
 * per card or row frame.
 *
 * @param {import('../../shell/types/user.types.js').TemplateComposition} composition
 * @param {string} name
 * @param {(bandId: string) => void} onSelect
 * @returns {HTMLFieldSetElement}
 */
export function createBandGroup(composition, name, onSelect) {
    const group = createChoiceGroup(name, 'band', 'pageForm.rowBand', composition.bands,
        (band) => appearanceDiagram(band.diagram), onSelect);
    group.querySelectorAll('.choice-input').forEach((input) => {
        input.name = name;
    });
    return group;
}

function createChoiceGroup(entry, kind, legendKey, options, diagramFor, onSelect) {
    const fieldset = document.createElement('fieldset');
    fieldset.className = `section-choice section-choice--${kind}`;
    const legend = document.createElement('legend');
    legend.dataset.i18n = legendKey;
    legend.textContent = t(legendKey);
    fieldset.appendChild(legend);
    const tiles = document.createElement('div');
    tiles.className = 'choice-tiles';
    for (const option of options) {
        const tile = document.createElement('label');
        tile.className = 'choice-tile';
        const input = document.createElement('input');
        input.type = 'radio';
        input.className = 'choice-input';
        input.name = `__wb_${kind}_${entry}`;
        input.value = option.id;
        input.addEventListener('change', () => {
            if (input.checked) onSelect(option.id);
        });
        const text = document.createElement('span');
        text.className = 'choice-label';
        text.textContent = option.label;
        tile.append(input, diagramFor(option), text);
        tiles.appendChild(tile);
    }
    fieldset.appendChild(tiles);
    return fieldset;
}

function iconButton(className, iconClass) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `btn btn-icon-only ${className}`;
    const icon = document.createElement('i');
    icon.className = `fa-solid ${iconClass}`;
    icon.setAttribute('aria-hidden', 'true');
    button.appendChild(icon);
    return button;
}

function layoutDiagram(diagram) {
    const element = document.createElement('span');
    element.className = 'layout-diagram';
    element.setAttribute('aria-hidden', 'true');
    const columns = Array.isArray(diagram?.columns) ? diagram.columns : [1];
    element.style.gridTemplateColumns = columns.map((weight) => `${weight}fr`).join(' ');
    columns.forEach((_, index) => {
        const slot = document.createElement('span');
        const kind = diagram?.slots?.[index] === DiagramSlot.TEXT ? DiagramSlot.TEXT : DiagramSlot.MEDIA;
        slot.className = `layout-diagram-slot layout-diagram-slot--${kind}`;
        element.appendChild(slot);
    });
    return element;
}

function appearanceDiagram(kind) {
    const known = [AppearanceDiagram.FILLED, AppearanceDiagram.PLAIN];
    const element = document.createElement('span');
    element.className = `appearance-diagram appearance-diagram--${known.includes(kind) ? kind : AppearanceDiagram.OUTLINE}`;
    element.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < 3; i++) {
        const line = document.createElement('span');
        line.className = 'appearance-diagram-line';
        element.appendChild(line);
    }
    return element;
}

function textButton(className, i18nKey) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.dataset.i18n = i18nKey;
    button.textContent = t(i18nKey);
    return button;
}

/**
 * The "more actions" disclosure of a card. Escape, moving focus out of it and a pointer press
 * outside it close it; the document listener for the latter is only attached while it is open.
 *
 * @param {{menuId: string, hintId?: string, actions: Array<{className: string, i18nKey: string, onSelect: () => void}>}} options
 * @returns {{wrap: HTMLDivElement, more: HTMLButtonElement, menu: HTMLDivElement, close: (focusToggle?: boolean) => void}}
 */
export function createCardMenu({menuId, hintId, actions}) {
    const wrap = document.createElement('div');
    wrap.className = 'section-item-menu-wrap';
    const more = iconButton('section-item-more', 'fa-ellipsis-vertical');
    more.setAttribute('aria-expanded', 'false');
    more.setAttribute('aria-controls', menuId);
    const menu = document.createElement('div');
    menu.className = 'section-item-menu';
    menu.id = menuId;
    menu.hidden = true;
    const onOutsidePointer = (event) => {
        if (!event.composedPath().includes(wrap)) close();
    };
    const close = (focusToggle = false) => {
        menu.hidden = true;
        more.setAttribute('aria-expanded', 'false');
        wrap.closest('.section-card').classList.remove('is-menu-open');
        document.removeEventListener('pointerdown', onOutsidePointer);
        if (focusToggle) more.focus();
    };
    for (const action of actions) {
        const button = textButton(`section-item-menu-action ${action.className}`, action.i18nKey);
        button.addEventListener('click', () => {
            close();
            action.onSelect();
        });
        menu.appendChild(button);
    }
    if (hintId) {
        const hint = document.createElement('p');
        hint.className = 'section-item-menu-hint';
        hint.id = hintId;
        hint.hidden = true;
        menu.appendChild(hint);
    }
    wrap.append(more, menu);
    more.addEventListener('click', () => {
        if (!menu.hidden) {
            close();
            return;
        }
        menu.hidden = false;
        more.setAttribute('aria-expanded', 'true');
        wrap.closest('.section-card').classList.add('is-menu-open');
        document.addEventListener('pointerdown', onOutsidePointer);
    });
    wrap.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !menu.hidden) close(true);
    });
    wrap.addEventListener('focusout', (event) => {
        if (!wrap.contains(event.relatedTarget)) close();
    });
    return {wrap, more, menu, close};
}

/**
 * The menu of an item card: the given extra actions, Duplicate and Delete, and a hint that
 * explains a disabled Delete.
 *
 * @param {{menuId: string, hintId: string, onDuplicate: () => void, onDelete: () => void, extraActions?: Array<{className: string, i18nKey: string, onSelect: () => void}>}} options
 */
export function createItemMenu({menuId, hintId, onDuplicate, onDelete, extraActions = []}) {
    const result = createCardMenu({
        menuId,
        hintId,
        actions: [
            ...extraActions,
            {className: 'section-item-duplicate', i18nKey: 'pageForm.itemDuplicate', onSelect: onDuplicate},
            {className: 'section-item-delete', i18nKey: 'pageForm.itemDelete', onSelect: onDelete},
        ],
    });
    result.menu.querySelector('.section-item-delete').setAttribute('aria-describedby', hintId);
    return result;
}

/**
 * Inline confirmation shown inside an item card before it is deleted. Escape cancels. The
 * question names the group for assistive technology; the section editor fills it in with the
 * item's current title.
 *
 * @param {{questionId: string, onConfirm: () => void, onCancel: () => void}} options
 * @returns {HTMLDivElement}
 */
export function createDeleteConfirm({questionId, onConfirm, onCancel}) {
    const confirm = document.createElement('div');
    confirm.className = 'section-item-confirm';
    confirm.setAttribute('role', 'group');
    confirm.setAttribute('aria-labelledby', questionId);
    confirm.hidden = true;
    const question = document.createElement('p');
    question.className = 'section-item-confirm-text';
    question.id = questionId;
    const accept = textButton('btn btn-sm section-item-confirm-delete', 'pageForm.itemDelete');
    const cancel = textButton('btn btn-sm btn-outline section-item-confirm-cancel', 'pageForm.itemDeleteCancel');
    accept.addEventListener('click', onConfirm);
    cancel.addEventListener('click', onCancel);
    confirm.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') onCancel();
    });
    confirm.append(question, accept, cancel);
    return confirm;
}

/**
 * The add control of a repeatable section: a chip with a plus icon and the item label, named
 * "Add {itemLabel}" for assistive technology, and a hint about the item count limits. It carries `data-list-key`, which tells the form serializer that the list
 * exists even while it has no items.
 *
 * @param {CompositionSection} section
 * @param {() => void} onAdd
 * @returns {HTMLDivElement}
 */
export function createItemAdder(section, onAdd) {
    const adder = document.createElement('div');
    adder.className = 'section-item-adder';
    adder.dataset.sectionId = section.id;
    adder.dataset.listKey = section.repeatable.dataKey;
    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'btn btn-outline section-item-add';
    add.dataset.sectionId = section.id;
    add.addEventListener('click', onAdd);
    const icon = document.createElement('span');
    icon.className = 'section-item-add-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '+';
    const label = document.createElement('span');
    label.className = 'section-item-add-label';
    add.append(icon, label);
    const hint = document.createElement('span');
    hint.className = 'section-item-add-hint';
    hint.id = `section-add-hint-${section.id}`;
    hint.dataset.sectionId = section.id;
    hint.hidden = true;
    adder.append(add, hint);
    return adder;
}
